import { getAnalytics } from "@tm/insurance/analytics";
import { formatDate } from "@tm/insurance/utils";
import { DATE_FORMATS, PARTNER } from "@tm/isomorphic-utils";
import {
  CheckButton,
  FlexComp,
  FormattedText,
  H6Tag,
  ImageComponent,
  LandingInput,
  RadioButton,
  SpanTag,
} from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";

import {
  getFormFieldErrorMsg,
  isDobValid,
  subtractYrsFromDate,
} from "@tm/insurance/utils";

import { trackEvent } from "@tm/insurance/analytics/xdm";
import {
  ArrowButtonWrap,
  GenderTypeItem,
  TravellerDetailsStyle,
} from "./AddTravellerDetail.style";
import TravellerDescp from "./TravellerDescpV2";
import { ITravellerDetailsFormV2, ITravellerDetailsProps } from "./type";

const minDate = subtractYrsFromDate(new Date(), 70).toISOString().split("T")[0];
const maxDate = new Date().toISOString().split("T")[0];
const analytics = getAnalytics();

const TravellerDetailsV2 = ({
  type,
  travellerIndex,
  count,
  travellerRef,
  isLoading,
  firstErrorIndex,
  travellerNumber,
  genderTypes,
  handleArrowButton,
  travellerSelected,
  handleGenderChange,
  handleTravellerSelect,
  selectedGender,
  saveFormValuesOnBlur,
  secondaryContactData,
  isSelected,
  travellerCount,
  isSavedTravellers,
  isValidForm,
  isEditable,
  isDomesticBooking,
  partner,
}: ITravellerDetailsProps) => {
  const device = useBreakpointV2();
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext<ITravellerDetailsFormV2>();

  const error = errors?.travellerData?.[travellerIndex];

  const { name, dob, gender, passport, nomineeName } =
    secondaryContactData || {};

  return (
    <TravellerDetailsStyle
      ref={travellerIndex === firstErrorIndex ? travellerRef : null}
      data-traveller-index={travellerIndex}
    >
      {travellerIndex === 0 && (
        <FlexComp
          alignItems="center"
          justifyContent="space-between"
          margin="0px 0px 20px 0px"
        >
          <FormattedText
            value={isSavedTravellers ? "Saved Travellers" : "Travellers"}
            baseStyle={{
              color: "#4A4A4A",
              fontSize: "14px",
              fontWeight: "700",
            }}
          />
          {travellerCount && travellerCount > 0 ? (
            <FormattedText
              value={
                count?.toString() + "/" + travellerCount?.toString() + " Added"
              }
              baseStyle={{
                color: isValidForm ? "#2B825E" : "#757575",
                fontSize: "14px",
                fontWeight: "700",
              }}
            />
          ) : null}
        </FlexComp>
      )}
      <Controller
        control={control}
        name={`travellerData.${travellerIndex}.isSelected`}
        render={({ field: { value } }) => (
          <CheckButton
            isChecked={value}
            isDisabled={!isEditable}
            handleChange={() => {
              if (!isEditable) return;
              if (
                !value &&
                getValues(`travellerData.${travellerIndex}.isSavedTraveller`)
              ) {
                if (getValues(`travellerData.${travellerIndex}.isEdited`)) {
                  analytics?.trackTravDetailsEvents(
                    {
                      actionType: "saved_traveller_edit_clicked",
                    },
                    "pageView",
                    type
                  );
                  trackEvent({
                    event_detail: {
                      event_type: "action",
                      event_name: "component_cta-clicked",
                      event_value: "saved_traveller_edit_clicked",
                    },
                  });
                } else {
                  analytics?.trackTravDetailsEvents(
                    {
                      actionType: "saved_traveller_clicked",
                    },
                    "pageView",
                    type
                  );
                  trackEvent({
                    event_detail: {
                      event_type: "action",
                      event_name: "component_cta-clicked",
                      event_value: "saved_traveller_clicked",
                    },
                  });
                }
              }
              handleTravellerSelect &&
                handleTravellerSelect(value, travellerIndex);
              saveFormValuesOnBlur();
            }}
          >
            {!travellerSelected ? (
              <TravellerDescp
                travellerIndex={travellerIndex}
                isSelected={isSelected}
                saveFormValuesOnBlur={saveFormValuesOnBlur}
                travellerNumber={travellerIndex + 1}
                genderTypeSelected={selectedGender}
                isDomesticBooking={isDomesticBooking}
                secondaryContactData={secondaryContactData}
                handleEditState={handleArrowButton}
                partner={partner}
              />
            ) : (
              <FlexComp alignItems="center" justifyContent="space-between">
                <H6Tag>{travellerNumber}</H6Tag>
                <ArrowButtonWrap onClick={handleArrowButton}>
                  <ImageComponent
                    width="9px"
                    height="9px"
                    margin="0px 0px 0px 0px"
                    src={
                      travellerSelected
                        ? partner === PARTNER.MYBIZ
                          ? "https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Up_Blue_db78b6056e.svg"
                          : "https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Up_Blue_21102e8ed6.svg"
                        : partner === PARTNER.MYBIZ
                        ? "https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_2e09571187.svg"
                        : "https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
                    }
                  />
                </ArrowButtonWrap>
              </FlexComp>
            )}
          </CheckButton>
        )}
      />
      <>
        {travellerSelected && (
          <React.Fragment>
            <FlexComp
              flexDirection={device == "lg" ? "row" : "column"}
              margin="16px 0 16px"
              gap="16px"
            >
              {name && (
                <Controller
                  control={control}
                  name={`travellerData.${travellerIndex}.name`}
                  rules={{
                    required: {
                      value: isSelected ? name?.isRequired ?? true : false,
                      message: getFormFieldErrorMsg("name"),
                    },
                    pattern: isSelected
                      ? {
                          value: new RegExp(name?.regex || ""),
                          message: getFormFieldErrorMsg(
                            "name",
                            name?.errorMessage
                          ),
                        }
                      : undefined,
                  }}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <LandingInput
                      flex="1"
                      requiredField={name?.isRequired}
                      placeholderTxt={name?.placeholder}
                      labelTxt={name?.title}
                      inputValue={value}
                      errorState={isSelected ? error?.name?.message : undefined}
                      disableState={isLoading}
                      handleChange={onChange}
                      handleBlur={() => {
                        saveFormValuesOnBlur(travellerIndex);
                        onBlur();
                      }}
                      autoComplete="off"
                      inputWidth="100%"
                      isDisabled={!isEditable}
                    />
                  )}
                />
              )}
              {dob && (
                <Controller
                  control={control}
                  name={`travellerData.${travellerIndex}.dob`}
                  rules={{
                    required:
                      isSelected && (dob?.isRequired ?? false)
                        ? {
                            value: true,
                            message: getFormFieldErrorMsg("dob"),
                          }
                        : false,
                    validate:
                      isSelected && (dob?.isRequired ?? false)
                        ? (value) =>
                            isDobValid(value, dob?.regex) ||
                            getFormFieldErrorMsg("dob", dob?.errorMessage)
                        : undefined,
                  }}
                  render={({ field: { value, onChange, onBlur } }) => {
                    return (
                      <LandingInput
                        flex="1"
                        placeholderTxt={dob?.placeholder}
                        requiredField={dob?.isRequired}
                        labelTxt={dob?.title}
                        inputType="date"
                        inputValue={formatDate(
                          value,
                          DATE_FORMATS["YYYY-MM-DD"]
                        )}
                        minValue={minDate}
                        maxValue={maxDate}
                        errorState={
                          isSelected ? error?.dob?.message : undefined
                        }
                        disableState={isLoading}
                        handleChange={onChange}
                        handleBlur={() => {
                          saveFormValuesOnBlur(travellerIndex);
                          onBlur();
                        }}
                        inputWidth="100%"
                      />
                    );
                  }}
                />
              )}
            </FlexComp>
            {gender && (
              <>
                <SpanTag
                  margin="0 0 8px"
                  fontSize="md"
                  fontWeight="bold"
                  color={theme.greyText}
                >
                  <FormattedText value={gender?.title} />
                  {gender?.isRequired && (
                    <SpanTag fontWeight="bold" color={theme.redText2}>
                      *
                    </SpanTag>
                  )}
                </SpanTag>
                <FlexComp
                  gap="8px"
                  margin={passport?.isEnabled ? "0 0 16px" : "0"}
                >
                  <Controller
                    control={control}
                    name={`travellerData.${travellerIndex}.gender`}
                    rules={{
                      required: {
                        value: isSelected ? gender?.isRequired ?? true : false,
                        message: getFormFieldErrorMsg("gender"),
                      },
                    }}
                    render={({ field: { value } }) => (
                      <>
                        {genderTypes?.map((item) => (
                          <GenderTypeItem
                            selected={item.label === value}
                            key={item.id}
                            onClick={() => {
                              handleGenderChange(item, travellerIndex);
                              saveFormValuesOnBlur(travellerIndex);
                            }}
                          >
                            <RadioButton activeBtn={item.label === value}>
                              <SpanTag
                                fontSize="xs"
                                fontWeight="bold"
                                color={
                                  item.label === value
                                    ? `var(--tm-accent-color)`
                                    : theme.greyText
                                }
                              >
                                {item.label}
                              </SpanTag>
                            </RadioButton>
                          </GenderTypeItem>
                        ))}
                      </>
                    )}
                  />
                </FlexComp>
              </>
            )}
            {passport && (
              <Controller
                control={control}
                name={`travellerData.${travellerIndex}.passport`}
                rules={{
                  required: {
                    value: isSelected ? passport?.isRequired ?? true : false,
                    message: getFormFieldErrorMsg("passport"),
                  },
                  pattern: isSelected
                    ? {
                        value: new RegExp(passport.regex || ""),
                        message: getFormFieldErrorMsg(
                          "passport",
                          passport?.errorMessage
                        ),
                      }
                    : undefined,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <LandingInput
                      requiredField={passport?.isRequired}
                      placeholderTxt={passport?.placeholder}
                      labelTxt={passport?.title}
                      errorState={
                        isSelected ? error?.passport?.message : undefined
                      }
                      inputValue={value}
                      disableState={isLoading}
                      handleChange={onChange}
                      handleBlur={() => {
                        saveFormValuesOnBlur(travellerIndex);
                        onBlur();
                      }}
                      autoComplete="off"
                      inputWidth="100%"
                      isDisabled={!isEditable}
                      margin="16px 0 0"
                    />
                  </>
                )}
              />
            )}
            {nomineeName && (
              <Controller
                control={control}
                name={`travellerData.${travellerIndex}.nomineeName`}
                rules={{
                  required: {
                    value: isSelected ? nomineeName?.isRequired ?? true : false,
                    message: getFormFieldErrorMsg("nomineeName"),
                  },
                  pattern: isSelected
                    ? {
                        value: new RegExp(nomineeName.regex || ""),
                        message: getFormFieldErrorMsg(
                          "nomineeName",
                          nomineeName?.errorMessage
                        ),
                      }
                    : undefined,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <LandingInput
                      requiredField={nomineeName?.isRequired}
                      placeholderTxt={nomineeName?.placeholder}
                      labelTxt={nomineeName?.title}
                      errorState={
                        isSelected ? error?.nomineeName?.message : undefined
                      }
                      inputValue={value}
                      disableState={isLoading}
                      handleChange={onChange}
                      handleBlur={() => {
                        saveFormValuesOnBlur(travellerIndex);
                        onBlur();
                      }}
                      autoComplete="off"
                      inputWidth="100%"
                      isDisabled={!isEditable}
                      margin="16px 0 0"
                    />
                  </>
                )}
              />
            )}
          </React.Fragment>
        )}
      </>

      {/* {travellerIndex === addedTravellerCount && (
                
            )} */}
    </TravellerDetailsStyle>
  );
};

export default TravellerDetailsV2;
